import type { ReactNode } from "react";
import { Button } from "../Button";
import { Modal } from "./Modal";

export type ConfirmModalTone = "danger" | "primary";

/** Optional identity chip (icon + label + value) shown under the description. */
export type ConfirmModalEntity = {
  icon: ReactNode;
  /** Small uppercase caption, e.g. "Coupon". */
  label: string;
  /** The identifier, rendered mono/bold, e.g. the code or name. */
  value: ReactNode;
};

export type ConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Header title, e.g. "Delete coupon" / "Publish store". */
  title: string;
  /** Header subtitle, e.g. "This action can't be undone." */
  subtitle?: string;
  /** Body paragraph explaining the consequence. */
  description?: ReactNode;
  /** Confirm button label, e.g. "Delete coupon" / "Publish". */
  confirmLabel: string;
  cancelLabel: string;
  /** Shown on the confirm button while `isSubmitting`. */
  loadingLabel?: string;
  isSubmitting?: boolean;
  closeLabel?: string;
  /**
   * Visual + semantic tone. "danger" (default) = destructive red tile + button;
   * "primary" = safe accent tile + primary button.
   */
  tone?: ConfirmModalTone;
  /** Override the header tile glyph (defaults: danger = trash, primary = check). */
  icon?: ReactNode;
  /**
   * Override the confirm button's leading icon. Defaults to a trash glyph for
   * danger and none for primary. Pass `null` to force no icon.
   */
  confirmIcon?: ReactNode | null;
  /** Optional identity chip for the subject being acted on. */
  entity?: ConfirmModalEntity;
  /** Extra custom body content rendered below the description + chip. */
  children?: ReactNode;
};

/**
 * Reusable confirmation modal matching the design's pattern: a toned icon tile
 * + title/subtitle, a body card with the consequence text and an optional
 * identity chip, and Cancel / confirm footer actions. Built on Modal so it
 * inherits the shared chrome, animation, and a11y.
 *
 * Use everywhere a confirm/delete prompt is needed — pass content, not markup:
 *   <ConfirmModal tone="danger" title="Delete coupon" … entity={{…}} />
 *   <ConfirmModal tone="primary" title="Publish store" confirmLabel="Publish" … />
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  description,
  confirmLabel,
  cancelLabel,
  loadingLabel,
  isSubmitting = false,
  closeLabel = "Close",
  tone = "danger",
  icon,
  confirmIcon,
  entity,
  children,
}: ConfirmModalProps) {
  const isDanger = tone === "danger";
  const tileIcon = icon ?? (isDanger ? <TrashIcon /> : <CheckIcon />);
  const buttonIcon =
    confirmIcon === undefined
      ? isDanger
        ? <TrashSmallIcon />
        : undefined
      : (confirmIcon ?? undefined);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      icon={tileIcon}
      iconTone={isDanger ? "danger" : "accent"}
      title={title}
      subtitle={subtitle}
      closeLabel={closeLabel}
      footerActions={
        <>
          <Button
            type="button"
            variant="outline"
            size="md"
            className="max-lg:min-h-10"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={isDanger ? "danger" : "primary"}
            size="md"
            className="max-lg:min-h-10"
            leadingIcon={buttonIcon}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && loadingLabel ? loadingLabel : confirmLabel}
          </Button>
        </>
      }
    >
      {description ? (
        <p className="text-[13.5px] leading-[1.55] text-[color:var(--text-secondary)]">
          {description}
        </p>
      ) : null}
      {entity ? (
        <div className="flex items-center gap-[11px] rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-[14px] py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] text-[color:var(--text-muted)]">
            {entity.icon}
          </span>
          <div className="min-w-0">
            <div className="mb-[3px] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[color:var(--text-disabled)]">
              {entity.label}
            </div>
            <div className="truncate font-mono text-[14px] font-bold tracking-[0.03em] text-[color:var(--text-primary)]">
              {entity.value}
            </div>
          </div>
        </div>
      ) : null}
      {children}
    </Modal>
  );
}

function TrashIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function TrashSmallIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
